// Implements https://cloud.google.com/storage/docs/json_api/v1/how-tos/resumable-upload

interface ProgressEvent {
  loaded: number;
  total: number;
}

interface UploadOptions {
  chunkSize: number;
  file: File;
  maxChunkErrors?: number;
  onProgress?: (event: ProgressEvent) => void;
  url: string;
}

interface Headers {
  [key: string]: string;
}

interface PutOptions {
  body: Blob;
  headers: Headers;
  onprogress: (event: ProgressEvent) => void;
}

interface PutResponse {
  status: number;
  headers: Headers;
}

const CHUNK_SIZE_MULTIPLE = 256 * 1024;

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export class InvalidChunkSizeError extends Error {
  constructor(chunkSize: number) {
    super(
      `Invalid chunk size ${chunkSize}, must be a multiple of ${CHUNK_SIZE_MULTIPLE}`,
    );
  }
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export class UploadError extends Error {
  constructor(chunkErrors: string[]) {
    super(
      `Upload failed after ${
        chunkErrors.length
      } errors in a single chunk: ${chunkErrors.join(', ')}`,
    );
  }
}

class BucketUpload {
  private canceled = false;
  private chunkSize: number;
  private file: File;
  private lastXhr: XMLHttpRequest | null = null;
  private maxChunkErrors: number;
  private onProgress: (event: ProgressEvent) => void;
  private url: string;

  constructor({
    chunkSize,
    file,
    maxChunkErrors,
    onProgress,
    url,
  }: UploadOptions) {
    this.chunkSize = chunkSize;
    this.file = file;
    this.maxChunkErrors = maxChunkErrors || 8;
    // eslint-disable-next-line: no-empty
    this.onProgress = onProgress || (() => {});
    this.url = url;

    if (chunkSize % CHUNK_SIZE_MULTIPLE !== 0 || chunkSize === 0) {
      throw new InvalidChunkSizeError(chunkSize);
    }
  }

  getUrl(): string {
    return this.url;
  }

  getFileSize(): number {
    return this.file.size;
  }

  async start(): Promise<void> {
    const { chunkSize, file } = this;
    let chunkErrors: string[] = [];
    let offset = 0;
    const onProgress = (event: ProgressEvent) => {
      this.onProgress({
        loaded: offset + event.loaded,
        total: file.size,
      });
    };

    while (offset < file.size) {
      const end = Math.min(offset + chunkSize, file.size);
      const chunkErrorsCount = chunkErrors.length;

      this.onProgress({ loaded: offset, total: file.size });

      if (chunkErrorsCount > 0) {
        if (chunkErrorsCount > this.maxChunkErrors) {
          throw new UploadError(chunkErrors);
        }

        // https://cloud.google.com/storage/docs/exponential-backoff
        const backoff = (2 ** (chunkErrorsCount - 1) + Math.random()) * 1000;
        // eslint-disable-next-line no-promise-executor-return -- Fix next time editing the file
        await new Promise((resolve) => setTimeout(resolve, backoff));
      }

      try {
        const response = await this.put({
          body: file.slice(offset, end),
          headers: {
            'Content-Range': `bytes ${offset}-${end - 1}/${file.size}`,
          },
          onprogress: onProgress,
        });

        if ([200, 201].includes(response.status) || this.canceled) {
          // As soon as we get 200 or 201 we're done, don't try to look at
          // headers.
          break;
        } else if ([500, 502, 503, 504].includes(response.status)) {
          chunkErrors.push(`status ${response.status}`);

          continue;
        }

        const { range } = response.headers;
        const rangeMatch = range?.match(/bytes=\d+-(\d+)/);
        // Range header may be absent if Google hasn't received anything yet.
        if (rangeMatch?.[1] != null) {
          chunkErrors = [];
          offset = Number.parseInt(rangeMatch[1], 10) + 1;
        }
      } catch (error) {
        chunkErrors.push(error.message);
      }
    }
  }

  cancel(): void {
    this.canceled = true;
    if (this.lastXhr !== null) {
      this.lastXhr.abort();
    }
  }

  private put({ body, headers, onprogress }: PutOptions): Promise<PutResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.lastXhr = xhr;

      xhr.open('PUT', this.url, true);
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.onload = () => {
        const responseHeaders = xhr
          .getAllResponseHeaders()
          .trim()
          .split(/[\r\n]+/)
          .reduce((result, line) => {
            const [key, value] = line.split(': ');
            return key && value
              ? { ...result, [key.toLowerCase()]: value }
              : result;
          }, {} as Headers);

        resolve({ status: xhr.status, headers: responseHeaders });
      };
      xhr.onerror = () => {
        reject(new Error('network error'));
      };
      xhr.upload.onprogress = onprogress;

      xhr.send(body);
    });
  }
}

export default BucketUpload;
